import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CountryPhoneInput from "@/components/CountryPhoneInput";
import { CylinderTypeSelect, useCylinderTypes } from "@/components/CylinderTypeSelect";
import { StationFields, geoPayload } from "@/components/household/StationFields";
import { useStations } from "@/components/supply/shared";

const pick = (hh) => ({
  household_name: hh?.household_name || "",
  phone: hh?.phone || "",
  email: hh?.email || "",
  address: hh?.address || "",
  notes: hh?.notes || "",
  state: hh?.state || "ACTIVE",
  cylinder_type_id: hh?.cylinder_type_id || "",
  station_id: hh?.station_id || "",
  lat: hh?.lat ?? "",
  lng: hh?.lng ?? "",
});

export const HouseholdEditDialog = ({ hh, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(pick(hh));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const cylTypes = useCylinderTypes();
  const [stations] = useStations();

  useEffect(() => { if (open) { setForm(pick(hh)); setErr(""); } }, [open, hh]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.household_name.trim()) { setErr("Full name is required"); return; }
    setSaving(true);
    try {
      await api.put(`/households/${hh.id}`, {
        ...form,
        household_name: form.household_name.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        ...geoPayload(form),
      });
      toast.success("Household updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="household-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit household</DialogTitle>
          <DialogDescription>Update contact details, location, notes and status.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Full name</Label>
            <Input value={form.household_name} onChange={set("household_name")} className="rounded-sm mt-1" data-testid="hh-edit-name" />
          </div>
          <div>
            <Label>Phone</Label>
            <div className="mt-1"><CountryPhoneInput value={form.phone} onChange={set("phone")} testId="hh-edit-phone" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={set("email")} className="rounded-sm mt-1" data-testid="hh-edit-email" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="hh-edit-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE" data-testid="hh-edit-state-active">Active</SelectItem>
                  <SelectItem value="SUSPENDED" data-testid="hh-edit-state-suspended">Suspended</SelectItem>
                  <SelectItem value="CLOSED" data-testid="hh-edit-state-closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Cylinder type</Label>
            <CylinderTypeSelect types={cylTypes} value={form.cylinder_type_id} onChange={set("cylinder_type_id")} testId="hh-edit-cylinder-type" />
            {hh?.cylinder_type_id && form.cylinder_type_id !== hh.cylinder_type_id && (
              <div className="text-[10px] text-amber-700 mt-1" data-testid="hh-edit-type-warning">Gas already on the meter stays; future payments use the new type's price.</div>
            )}
          </div>
          <StationFields form={form} setForm={setForm} stations={stations} testPrefix="hh-edit" />
          <div>
            <Label>Address / location</Label>
            <Input value={form.address} onChange={set("address")} className="rounded-sm mt-1" data-testid="hh-edit-address" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={3} className="rounded-sm mt-1" data-testid="hh-edit-notes" />
          </div>
          {err && <div className="text-xs text-red-600" data-testid="hh-edit-error">{err}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="hh-edit-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm" data-testid="hh-edit-save">{saving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
